import { useState } from "react";
import { Button } from "flowbite-react";
import {
  ArrowRightLeft, CreditCard, ShieldCheck, User, X, Clock,
  MoveRight, Loader2,
} from "lucide-react";
import { RefuseConfirmation } from "@/features/employeeTransfer/components/InfoStep/RefuseConfirmation";
import { InlineErrorBanner } from "@/features/employeeTransfer/components/InlineErrorBanner";
import type { TransferRequest } from "@/features/employeeTransfer/types/employeeTransfer.types";
import { H2 } from "@/shared/ui/typographies/h2";
import { P } from "@/shared/ui/typographies/paragraph";

type InfoStepProps = {
  transferRequest: TransferRequest
  isMutating: boolean
  inlineError: string | null
  onConfirm: () => void
  onRefuse: () => void
};

const formatExpiryDate = (isoDate: string): string => {
  return new Date(isoDate).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const TRANSFER_ITEMS = [
  { icon: User, label: "Vos données personnelles" },
  { icon: CreditCard, label: "Votre carte de paiement et vos paramètres" },
  { icon: ArrowRightLeft, label: "Vos soldes actifs" },
] as const;

export const InfoStep = ({
  transferRequest,
  isMutating,
  inlineError,
  onConfirm,
  onRefuse,
}: InfoStepProps) => {
  const [showRefuseConfirmation, setShowRefuseConfirmation] = useState(false);

  const {
    source_organization_name: sourceOrg,
    target_organization_name: targetOrg,
    expires_at: expiresAt,
  } = transferRequest;

  const formattedExpiry = formatExpiryDate(expiresAt);

  return (
    <div className="space-y-8">
      <H2>Demande de transfert de compte</H2>

      <P size="sm" color="base" className="text-pretty tracking-wider">
        Le CSE
        {" "}
        <strong className="text-gray-900 dark:text-white">{targetOrg}</strong>
        {" "}
        souhaite transférer votre compte depuis
        {" "}
        <strong className="text-gray-900 dark:text-white">{sourceOrg}</strong>
        .
      </P>

      <div className="flex justify-between items-stretch gap-4 -mt-4">
        <div className="flex-1 flex items-center justify-center text-gray-600 dark:text-gray-400 border border-gray-200 bg-gray-50 dark:border-gray-800 dark:bg-gray-800/50 rounded-md py-2 px-4">
          {sourceOrg}
        </div>
        <MoveRight size={16} className="shrink-0 self-center" />
        <div className="flex-1 flex items-center justify-center text-teal-800 dark:text-teal-200 border border-teal-100 bg-teal-50/50 p-4 dark:border-teal-900 dark:bg-teal-950/20 rounded-md py-2 px-4">
          {targetOrg}
        </div>
      </div>

      <div className="rounded-lg border border-teal-100 bg-teal-50/50 p-4 dark:border-teal-900 dark:bg-teal-950/20">
        <P size="sm" className="mb-3 font-medium text-teal-800 dark:text-teal-200">
          Ce qui sera transféré :
        </P>
        <ul className="space-y-2">
          {TRANSFER_ITEMS.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-2 text-sm text-teal-800 dark:text-teal-200">
              <Icon size={16} className="shrink-0" />
              {label}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex items-start gap-2 rounded-lg tracking-wider text-pretty border border-warning bg-warning/10 p-3 dark:border-warning dark:bg-warning/20">
        <ShieldCheck size={18} strokeWidth={2} className="mt-0.5 shrink-0 text-amber-700 dark:text-amber-300" />
        <p className="text-sm text-pretty tracking-wider text-amber-700 dark:text-amber-300">
          Si vous n'êtes pas à l'origine de cette demande ou si elle vous semble suspecte,
          vous pouvez la refuser en toute sécurité. Votre compte restera inchangé.
        </p>
      </div>

      <div className="flex items-center gap-2 text-xs text-dark dark:text-white">
        <Clock size={14} className="shrink-0" />
        {`Ce lien expire le ${formattedExpiry}`}
      </div>

      {inlineError && <InlineErrorBanner message={inlineError} />}

      {showRefuseConfirmation
        ? (
          <RefuseConfirmation
            sourceOrganization={sourceOrg}
            isMutating={isMutating}
            onConfirmRefuse={onRefuse}
            onCancel={() => setShowRefuseConfirmation(false)}
          />
        )
        : (
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              color="primary"
              className="flex-1"
              onClick={onConfirm}
              disabled={isMutating}
            >
              {isMutating
                ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                )
                : "Confirmer le transfert"}
            </Button>
            <Button
              color="error"
              className="flex-1"
              onClick={() => setShowRefuseConfirmation(true)}
              disabled={isMutating}
            >
              <X size={16} className="mr-2" />
              Refuser le transfert
            </Button>
          </div>
        )}
    </div>
  );
};
